import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { FiExternalLink } from "react-icons/fi";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import Footer from "./Footer";
import OneProduct from "./OneProduct";

export default function Products({ isHomepagePreview }) {
  const sectionRef = useRef(null);
  const appContext = useContext(AppContext);
  const isAllProductsView = appContext.navItem === "products";

  const [ready, setReady] = useState(false);

  // Initialize data fetching following the exact flow of NewsBanner
  async function init() {
    setReady(false);
    if (typeof appContext.getAllProducts === "function") {
      await appContext.getAllProducts();
    }
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  // Sort products by dateCreated descending
  const sortedProducts = useMemo(() => {
    const allProds = Array.isArray(appContext.allProducts)
      ? [...appContext.allProducts]
      : Array.isArray(appContext.products)
      ? [...appContext.products]
      : [];
    return allProds.sort(
      (a, b) => Number(b?.dateCreated || 0) - Number(a?.dateCreated || 0)
    );
  }, [appContext.allProducts, appContext.products]);

  // Intersection Observer setup for animation triggers
  useEffect(() => {
    if (!ready || !sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("reveal-active");
          }
        });
      },
      { threshold: 0.1 }
    );

    const revealElements = sectionRef.current.querySelectorAll(".reveal-item");
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [ready, isAllProductsView, isHomepagePreview]);

  if (!ready) {
    return (
      <div className="container py-5 text-center">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <>
      <style>{`
        .p-section {
          background-color: var(--bg-dark, #0a1118);
          padding: clamp(80px, 12vh, 150px) 25px;
          overflow: hidden;
        }

        .p-header { 
          max-width: 1200px; 
          margin: 0 auto 80px auto; 
          font-family: 'Inter', system-ui, sans-serif; 
        }

        .w-header-title {
          font-size: clamp(2.4rem, 6vw, 4.8rem);
          font-weight: 900;
          line-height: 1;
          letter-spacing: -0.06em;
          color: #fff;
        }

        /* --- FEATURE MODE --- */
        .p-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 120px;
        }

        .p-item {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: clamp(40px, 8vw, 100px);
          align-items: center;
        }

        .p-item:nth-child(even) { direction: rtl; }
        .p-item:nth-child(even) .p-text-box { direction: ltr; }

        .p-img-wrapper {
          position: relative;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.02);
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }

        .p-img-wrapper img {
          width: 100%; 
          height: auto; 
          border-radius: 12px;
          transition: all 0.6s cubic-bezier(0.23, 1, 0.32, 1);
        }

        .p-prod-name { color: #fff; font-size: clamp(2rem, 4vw, 2.8rem); font-weight: 800; margin-bottom: 8px; }
        .p-prod-tagline { color: var(--secondaryColor, #38bdf8); font-weight: 600; margin-bottom: 25px; display: block; }
        .p-prod-desc { color: #94a3b8; font-size: 1.1rem; line-height: 1.7; margin-bottom: 35px; max-width: 500px; }

        .p-feat-single { display: flex; align-items: center; gap: 10px; color: #fff; margin-bottom: 12px; font-size: 0.95rem; }
        .p-feat-single svg { color: var(--secondaryColor, #38bdf8); }

        .p-cta-btn {
          display: inline-flex; align-items: center; gap: 12px; padding: 16px 35px;
          background: var(--secondaryColor, #38bdf8); color: #fff; border-radius: 4px;
          font-weight: 700; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; transition: 0.3s;
          text-decoration: none;
        }

        .view-more-container { text-align: center; margin-top: 100px; }
        .btn-outline {
          background: transparent; border: 1px solid rgba(255,255,255,0.2); color: #fff; padding: 15px 40px; border-radius: 50px; cursor: pointer; transition: 0.3s;
        }
        .btn-outline:hover { background: #fff; color: #000; border-color: #fff; }

        /* --- ALL PRODUCTS VIEW --- */
        .all-prod-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 30px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .minimal-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px;
          padding: 24px;
          transition: 0.4s;
          display: flex;
          flex-direction: column;
          height: 100%;
        }

        .minimal-card:hover {
          background: rgba(255,255,255,0.06);
          border-color: var(--secondaryColor, #38bdf8);
          transform: translateY(-10px);
        }

        .minimal-img { width: 100%; height: 220px; object-fit: cover; border-radius: 16px; margin-bottom: 20px; }
        .minimal-name { color: #fff; font-size: 1.5rem; font-weight: 700; margin-bottom: 5px; }
        .minimal-desc { color: #94a3b8; font-size: 0.9rem; line-height: 1.6; margin-bottom: 20px; flex-grow: 1; }
        
        .minimal-cta {
          color: var(--secondaryColor, #38bdf8);
          font-weight: 700;
          font-size: 0.8rem;
          text-transform: uppercase;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          text-decoration: none;
        }

        /* --- Animations --- */
        .reveal-item { opacity: 0; transform: translateY(30px); transition: all 0.8s cubic-bezier(0.2, 1, 0.3, 1); }
        .reveal-active { opacity: 1; transform: translateY(0); }

        @media (max-width: 991px) {
          .p-item { grid-template-columns: 1fr; text-align: center; }
          .p-item:nth-child(even) { direction: ltr; }
          .p-prod-desc { margin: 0 auto 35px; }
          .all-prod-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      <section className="p-section" ref={sectionRef}>
        <div className="p-header reveal-item">
          <header>
            <h2 className="w-header-title text-secondary text-start">
              Our Products
            </h2>
          </header>
          <h2 className="p-title" style={{ color: "#fff", marginTop: "10px" }}>
            {isAllProductsView
              ? "Explore our complete digital suite."
              : "Modern Solutions for Digital Advancement."}
          </h2>
        </div>

        {isAllProductsView ? (
          /* --- CLEAN ALL PRODUCTS LIST --- */
          <div className="all-prod-grid">
            {sortedProducts.length > 0 ? (
              sortedProducts.map((item) => (
                <OneProduct key={item.id || item.uuid} data={item} isMinimal={true} />
              ))
            ) : (
              <div className="col-12 text-center text-muted py-5">
                <h6>No products found</h6>
              </div>
            )}
          </div>
        ) : (
          /* --- FEATURE MODE (Home / Landing View) --- */
          <>
            <div className="p-grid">
              {sortedProducts.length > 0 ? (
                sortedProducts.map((item) => (
                  <OneProduct key={item.id || item.uuid} data={item} isMinimal={false} />
                ))
              ) : (
                <div className="text-center text-muted py-5">
                  <h6>No products found</h6>
                </div>
              )}
            </div>

            <div className="view-more-container reveal-item">
              <button
                className="btn-outline"
                onClick={() => appContext.navTo({ item: "products" })}
              >
                View All Products{" "}
                <FiExternalLink style={{ marginLeft: "10px" }} />
              </button>
            </div>
          </>
        )}
      </section>

      {appContext.navItem === "products" ? <Footer /> : ""}
    </>
  );
}